import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from './UserContext';

const LibraryContext = createContext();

export function LibraryProvider({ children }) {
  const { user } = useUser();
  const [library, setLibrary] = useState([]);
  const [readingProgress, setReadingProgress] = useState({});
  const [swipedBooks, setSwipedBooks] = useState([]);

  // Sync avec Firestore
  useEffect(() => {
    if (!user) {
      setLibrary([]);
      setReadingProgress({});
      setSwipedBooks([]);
      return;
    }

    const unsubscribe = onSnapshot(doc(db, 'userLibraries', user.id), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setLibrary(data.library || []);
        setReadingProgress(data.readingProgress || {});
        setSwipedBooks(data.swipedBooks || []);
      }
    });

    return () => unsubscribe();
  }, [user]);

  const saveToFirestore = useCallback(async (updates) => {
    if (!user) return;
    await setDoc(doc(db, 'userLibraries', user.id), updates, { merge: true });
  }, [user]);

  const addToLibrary = useCallback(async (book) => {
    if (library.find(b => b.id === book.id)) return;
    const bookData = {
      id: book.id,
      title: book.title,
      author: book.author,
      coverGradient: book.coverGradient,
      coverImage: book.coverImage || null,
      genre: book.genre,
    };
    const updated = [bookData, ...library];
    setLibrary(updated);
    await saveToFirestore({ library: updated });
  }, [library, saveToFirestore]);

  const removeFromLibrary = useCallback(async (bookId) => {
    const updated = library.filter(b => b.id !== bookId);
    setLibrary(updated);
    await saveToFirestore({ library: updated });
  }, [library, saveToFirestore]);

  const isInLibrary = useCallback((bookId) => {
    return library.some(b => b.id === bookId);
  }, [library]);

  const markAsSwiped = useCallback(async (bookId) => {
    if (swipedBooks.includes(bookId)) return;
    const updated = [...swipedBooks, bookId];
    setSwipedBooks(updated);
    await saveToFirestore({ swipedBooks: updated });
  }, [swipedBooks, saveToFirestore]);

  const updateProgress = useCallback(async (bookId, chapterIndex) => {
    const updated = { ...readingProgress, [bookId]: chapterIndex };
    setReadingProgress(updated);
    await saveToFirestore({ readingProgress: updated });
  }, [readingProgress, saveToFirestore]);

  const getProgress = useCallback((bookId) => {
    if (readingProgress[bookId] === undefined) return -1; // Jamais ouvert
    return readingProgress[bookId];
  }, [readingProgress]);

  return (
    <LibraryContext.Provider value={{
      library, addToLibrary, removeFromLibrary, isInLibrary,
      swipedBooks, markAsSwiped,
      updateProgress, getProgress,
    }}>
      {children}
    </LibraryContext.Provider>
  );
}

export function useLibrary() {
  return useContext(LibraryContext);
}